import { action, makeObservable, observable } from "mobx";
import { LLMExecutorStepPromptConfiguration } from "./LLMExecutorStepPromptConfiguration";
import type { ILLMExecutorStepPromptConfiguration } from "./LLMExecutorStepPromptConfiguration";
import type { LLMGenerationSettings } from "../LLMGenerationSettings";
import { ConditionPrimitive } from "./Conditions/ConditionPrimitive";
import { v4 } from "uuid";
import { Condition } from "./Conditions/Condition";

interface LLMExecutorStepBase {
    id: string;
    name: string;
    isEntryPoint: boolean;
    llmModelName?: string;
    modelOptions?: LLMExecutorStepModelOptions;
}

export interface LLMExecutorStepModelOptions
    extends Omit<LLMGenerationSettings, "modelName"> {}

export interface LLMExecutorStepPrompt extends LLMExecutorStepBase {
    type: LLMExecutorStepType.ExecutePrompt;
    configuration: ILLMExecutorStepPromptConfiguration;
}

export type ILLMExecutorStep = LLMExecutorStepPrompt;

export enum LLMExecutorStepType {
    ExecutePrompt = "ExecutePrompt",
}

export class LLMExecutorStep implements ILLMExecutorStep {
    /**
     * A unique identifier for the step. This id does not change and is used for react key purposes.
     */
    public readonly reactKey: string;
    /**
     * A unique identifier for the step. This id is subject to change as it is computed from the step's name.
     */
    @observable id: string;
    @observable name: string;
    @observable isEntryPoint: boolean;
    @observable llmModelName?: string;
    @observable modelOptions?: LLMExecutorStepModelOptions;

    @observable type: LLMExecutorStepType;
    @observable configuration: LLMExecutorStepPromptConfiguration;

    /**
     * Creates a new LLMExecutorStep for the given step type and initializes the default configuration
     * for that type. A unique version 4 uuid is assigned.
     * @param {LLMExecutorStepType} type
     */
    constructor(type: LLMExecutorStepType);
    /**
     * Creates a new `LLMExecutorStep` instance from a json object
     * @param {ILLMExecutorStep} step a json object representing an `ILLMExecutorStep`
     */
    constructor(step: ILLMExecutorStep);
    constructor(stepOrType: ILLMExecutorStep | LLMExecutorStepType) {
        makeObservable(this);

        this.reactKey = v4();

        if (typeof stepOrType === "object") {
            const step = stepOrType as ILLMExecutorStep;
            Object.assign(this, step);
            this.configuration = new LLMExecutorStepPromptConfiguration(
                step.configuration,
            );
        } else {
            this.id = "";
            this.name = "";
            this.isEntryPoint = false;
            this.modelOptions = undefined;
            this.type = stepOrType;
            this.configuration =
                LLMExecutorStep.createDefaultConfigurationForStepType(
                    stepOrType,
                );
        }
    }

    @action
    public setName(name: string) {
        this.name = name;
        this.id = LLMExecutorStep.slugifyWithRandomSuffix(name);
    }

    @action
    public setIsEntryPoint(isEntryPoint: boolean) {
        this.isEntryPoint = isEntryPoint;
    }

    @action
    public setLLMModelName(name?: string) {
        this.llmModelName = name;
        this.modelOptions = name ? { temperature: 0 } : undefined;
    }

    @action
    public setModelGenerationSettings(
        settings?: LLMGenerationSettings | null,
    ) {
        this.llmModelName = settings?.modelName ?? undefined;
        this.modelOptions =
            settings?.temperature != null || settings?.thinkingEffortLevel
                ? {
                      temperature: settings.temperature ?? undefined,
                      thinkingEffortLevel:
                          settings.thinkingEffortLevel ?? undefined,
                  }
                : undefined;
    }

    @action
    public setModelTemperature(temperature?: number) {
        this.modelOptions = { temperature: temperature ?? 0 };
    }

    @action
    public setModelThinkingEffortLevel(thinkingEffortLevel?: string) {
        this.modelOptions = thinkingEffortLevel
            ? { thinkingEffortLevel }
            : { temperature: 0 };
    }

    @action
    public updatePromptStepId(oldStepId: string, newStepId: string) {
        switch (this.type) {
            case LLMExecutorStepType.ExecutePrompt:
                this.configuration.setPrompt(
                    this.configuration.prompt.replace(oldStepId, newStepId),
                );

                if (this.configuration.actions) {
                    for (const action of this.configuration.actions) {
                        if (action.conditions) {
                            for (const condition of action.conditions) {
                                if (condition instanceof ConditionPrimitive) {
                                    condition.propertyName =
                                        condition.propertyName.replace(
                                            oldStepId,
                                            newStepId,
                                        );
                                }
                            }
                        }

                        if (
                            "writeFromVariable" in
                            action.thenActionConfiguration
                        ) {
                            action.thenActionConfiguration.writeFromVariable =
                                action.thenActionConfiguration.writeFromVariable.replace(
                                    oldStepId,
                                    newStepId,
                                );
                        }
                        if ("stepId" in action.thenActionConfiguration) {
                            action.thenActionConfiguration.stepId =
                                action.thenActionConfiguration.stepId.replace(
                                    oldStepId,
                                    newStepId,
                                );
                        }
                    }
                }

                break;
            default:
                break;
        }
    }

    /**
     * Updates the property names on all conditions and actions with `newPropertyName` that match with `oldPropertyName`.
     * Will only perform the update if the condition/action is targeting the provided `stepId`.
     * @param stepId The step id to target
     * @param oldPropertyName The (short) old property name to update
     * @param newPropertyName The (short) new property name to update to
     * @note (short) in this context refers to the property name without the step id prefix
     */
    @action
    public updatePropertyNames(
        stepId: string,
        oldPropertyName: string,
        newPropertyName: string,
    ) {
        const longOldPropertyName = `${stepId}.${oldPropertyName}`;
        const longNewPropertyName = `${stepId}.${newPropertyName}`;

        switch (this.type) {
            case LLMExecutorStepType.ExecutePrompt:
                if (this.configuration.actions) {
                    for (const action of this.configuration.actions) {
                        if (action.conditions) {
                            for (const condition of action.conditions) {
                                if (!(condition instanceof ConditionPrimitive))
                                    continue;

                                // If the condition isn't targeting the step and property we're updating, skip it.
                                if (
                                    condition.propertyName !==
                                    longOldPropertyName
                                )
                                    continue;

                                condition.setPropertyName(longNewPropertyName);
                                condition.setShortPropertyName(newPropertyName);
                            }
                        }

                        // If the THEN part of the action is writing from the target step and property, update it.
                        if (
                            action.thenActionConfiguration[
                                "writeFromVariable"
                            ] === longOldPropertyName
                        ) {
                            action.setWriteFromVariable(longNewPropertyName);
                            action.setShortWriteFromVariable(newPropertyName);
                        }
                    }
                }

                // update prompt vars
                if (this.configuration.prompt.includes(longOldPropertyName)) {
                    this.configuration.prompt =
                        this.configuration.prompt.replace(
                            longOldPropertyName,
                            longNewPropertyName,
                        );
                }

                break;
            default:
                break;
        }
    }

    public getPropertyName(propertyName: string) {
        return `${this.id}.${propertyName}`;
    }

    /**
     * Returns a set of all variables in the prompt and property names
     * used in if conditions for this step.
     */
    public extractVariableAndPropertyNames() {
        var promptVariables =
            this.configuration.prompt
                .match(/{{(.*?)}}/g)
                ?.map((v) => v.replace("{{", "").replace("}}", "")) ?? [];

        var propertyNames = this.configuration.actions
            .flatMap((action) => action.conditions)
            .flatMap((condition) => Condition.ExtractPropertyNames(condition));

        return Array.from(new Set([...promptVariables, ...propertyNames]));
    }

    private static createDefaultConfigurationForStepType(
        type: LLMExecutorStepType,
    ) {
        switch (type) {
            case LLMExecutorStepType.ExecutePrompt:
                return new LLMExecutorStepPromptConfiguration();
        }
    }

    private static slugifyWithRandomSuffix(str) {
        const slug = str.toLowerCase().trim().replace(/\s+/g, "-"); // replace all whitespace with hyphens

        const randomSuffix = Math.random().toString(36).substring(2, 6); // 4 random characters
        return `${slug}-${randomSuffix}`;
    }
}
